import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import InventoryAPI from "../../services/inventoryService";
import {
  getServiceItemTemplates,
  createServiceItemTemplate,
  updateServiceItemTemplate,
  deleteServiceItemTemplate
} from "../../services/servicesApiV2";

/**
 * ProductItemTemplateModal
 *
 * Manages material item templates (type='material') on a master service.
 * Each row: Label | Brand | Product | Delete
 */
const ProductItemTemplateModal = ({
  show,
  onHide,
  masterServiceId,
  installerId,
  categoryName,
  serviceName,
  onTemplatesChanged
}) => {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [productsByBrand, setProductsByBrand] = useState({});
  const [stockLevels, setStockLevels] = useState({});
  const [unitLabel, setUnitLabel] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [originalItems, setOriginalItems] = useState([]);

  useEffect(() => {
    if (show && masterServiceId) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, masterServiceId]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [templates, stockData, allocatedData, categories] = await Promise.all([
        getServiceItemTemplates(masterServiceId),
        InventoryAPI.getStockLevels(installerId),
        InventoryAPI.getAllocatedInventory(installerId).catch(() => []),
        InventoryAPI.getServiceCategories().catch(() => [])
      ]);

      // Build stock lookup: { productId: { onHand, allocated } }
      const stockMap = {};
      (stockData || []).forEach(s => {
        const pid = s.product_id || s.id;
        stockMap[pid] = { onHand: parseFloat(s.quantity_on_hand) || 0, allocated: 0 };
      });
      (allocatedData || []).forEach(a => {
        if (stockMap[a.product_id]) {
          stockMap[a.product_id].allocated = parseFloat(a.allocated_quantity) || 0;
        } else {
          stockMap[a.product_id] = { onHand: 0, allocated: parseFloat(a.allocated_quantity) || 0 };
        }
      });
      setStockLevels(stockMap);

      // Determine unit label from category config
      const catConfig = (categories || []).find(c =>
        (c.name || c.category_name || '').toLowerCase() === (categoryName || '').toLowerCase()
      );
      setUnitLabel(catConfig?.default_unit_code || '');

      // Use stock data as product source (it includes product metadata)
      const allProducts = stockData || [];

      // Filter templates to material type only.
      // Items without an explicit type default to material (DB default).
      const materialTemplates = (templates || []).filter(t =>
        t.type !== 'work'
      );

      // Filter products by category
      const categoryProducts = (allProducts || []).filter(p => p.category_name === categoryName);
      const sorted = categoryProducts.sort((a, b) => {
        const brandA = (a.brand || '').toLowerCase();
        const brandB = (b.brand || '').toLowerCase();
        if (brandA !== brandB) return brandA.localeCompare(brandB);
        const nameA = (a.product_name || a.name || '').toLowerCase();
        const nameB = (b.product_name || b.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });

      const uniqueBrands = [...new Set(sorted.map(p => p.brand).filter(Boolean))].sort();
      const byBrand = {};
      sorted.forEach(p => {
        const brand = p.brand || '';
        if (!byBrand[brand]) byBrand[brand] = [];
        byBrand[brand].push(p);
      });

      setProducts(sorted);
      setBrands(uniqueBrands);
      setProductsByBrand(byBrand);

      // Map templates to editable rows
      const rows = materialTemplates.map(t => ({
        _key: t.id,
        id: t.id,
        label: t.label || '',
        brand: t.recommended_product_brand || '',
        productId: t.recommended_product_id || null,
        productName: t.recommended_product_name || '',
        sort_order: t.sort_order || 0
      }));

      setItems(rows);
      setOriginalItems(JSON.parse(JSON.stringify(rows)));
    } catch (err) {
      console.error('Error loading item templates:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const addRow = () => {
    setItems(prev => [...prev, {
      _key: `new-${Date.now()}`,
      id: null,
      label: '',
      brand: '',
      productId: null,
      productName: '',
      sort_order: prev.length
    }]);
  };

  const removeRow = (index) => {
    setItems(prev => prev.filter((_, i) => i !== index));
    setValidationErrors(prev => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const updateRow = (index, field, value) => {
    setItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      // When brand changes, clear product selection
      if (field === 'brand') {
        updated[index].productId = null;
        updated[index].productName = '';
      }

      // When product changes, update product name
      if (field === 'productId') {
        const productId = value ? parseInt(value, 10) : null;
        updated[index].productId = productId;
        if (productId) {
          const product = products.find(p => (p.product_id || p.id) === productId);
          updated[index].productName = product ? (product.product_name || product.name) : '';
        } else {
          updated[index].productName = '';
        }
      }

      return updated;
    });

    // Clear validation error for this row
    if (validationErrors[index]) {
      setValidationErrors(prev => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
    }
  };

  const validate = () => {
    const errors = {};
    const labels = {};

    items.forEach((item, index) => {
      if (!item.label.trim()) {
        errors[index] = 'Label is required';
        return;
      }
      const lower = item.label.trim().toLowerCase();
      if (labels[lower] !== undefined) {
        errors[index] = 'Duplicate label';
        errors[labels[lower]] = 'Duplicate label';
      }
      labels[lower] = index;
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setSaving(true);
    try {
      const originalMap = {};
      originalItems.forEach(o => { if (o.id) originalMap[o.id] = o; });

      const currentIds = new Set(items.filter(i => i.id).map(i => i.id));

      // Delete removed items
      for (const orig of originalItems) {
        if (orig.id && !currentIds.has(orig.id)) {
          await deleteServiceItemTemplate(orig.id);
        }
      }

      // Create new items, update changed items
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (!item.id) {
          // New item
          await createServiceItemTemplate(masterServiceId, {
            label: item.label.trim(),
            type: 'material',
            recommended_product_id: item.productId || null,
            recommended_product_name: item.productName || null,
            recommended_product_brand: item.brand || null,
            sort_order: i
          });
        } else {
          // Existing item — check if changed
          const orig = originalMap[item.id];
          if (orig &&
            (orig.label !== item.label.trim() ||
              orig.brand !== item.brand ||
              orig.productId !== item.productId)) {
            await updateServiceItemTemplate(item.id, {
              label: item.label.trim(),
              recommended_product_id: item.productId || null,
              recommended_product_name: item.productName || null,
              recommended_product_brand: item.brand || null
            });
          }
        }
      }

      if (onTemplatesChanged) onTemplatesChanged();
      onHide();
    } catch (err) {
      console.error('Error saving item templates:', err);
      setError('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fa-sharp fa-regular fa-cube mr-2"></i>
          Items for {categoryName} {serviceName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" className="mr-2" />
            Loading...
          </div>
        ) : error ? (
          <div className="alert alert-danger">{error}</div>
        ) : (
          <>
            <p className="text-muted small mb-3">
              Define the product items for this service. Each item represents a section or area
              that needs a product assigned (e.g., "Windshield", "Front Doors").
            </p>

            {items.length === 0 ? (
              <div className="text-center text-muted py-3">
                No product items defined yet.
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm mb-0">
                  <thead className="thead-light">
                    <tr>
                      <th style={{ width: '30%' }}>Label</th>
                      <th style={{ width: '25%' }}>Brand</th>
                      <th style={{ width: '37%' }}>Product</th>
                      <th style={{ width: '8%' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => (
                      <tr key={item._key}>
                        <td>
                          <Form.Control
                            size="sm"
                            type="text"
                            value={item.label}
                            onChange={(e) => updateRow(index, 'label', e.target.value)}
                            placeholder="e.g., Windshield"
                            isInvalid={!!validationErrors[index]}
                          />
                          {validationErrors[index] && (
                            <Form.Text className="text-danger">{validationErrors[index]}</Form.Text>
                          )}
                        </td>
                        <td>
                          <Form.Control
                            as="select"
                            size="sm"
                            value={item.brand}
                            onChange={(e) => updateRow(index, 'brand', e.target.value)}
                          >
                            <option value="">--</option>
                            {brands.map(b => (
                              <option key={b} value={b}>{b}</option>
                            ))}
                          </Form.Control>
                        </td>
                        <td>
                          <Form.Control
                            as="select"
                            size="sm"
                            value={item.productId || ''}
                            onChange={(e) => updateRow(index, 'productId', e.target.value)}
                            disabled={!item.brand}
                          >
                            <option value="">--</option>
                            {(productsByBrand[item.brand] || []).map(p => {
                              const pid = p.product_id || p.id;
                              const pname = p.product_name || p.name;
                              const stock = stockLevels[pid];
                              const unit = unitLabel ? ` ${unitLabel}` : '';
                              const stockText = stock
                                ? ` (${stock.onHand}${unit} in stock, ${stock.allocated} allocated)`
                                : '';
                              return (
                                <option key={pid} value={pid}>{pname}{stockText}</option>
                              );
                            })}
                          </Form.Control>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-sm btn-link text-danger p-0"
                            onClick={() => removeRow(index)}
                            title="Remove"
                          >
                            <i className="fa-sharp fa-regular fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <button
              type="button"
              className="btn btn-sm btn-outline-primary mt-2"
              onClick={addRow}
            >
              <i className="fa-sharp fa-regular fa-plus mr-1"></i> Add Item
            </button>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={loading || saving}>
          {saving ? (
            <>
              <Spinner animation="border" size="sm" className="mr-1" />
              Saving...
            </>
          ) : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ProductItemTemplateModal;
